
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Share2, Check, Info, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { 
  Dialog,
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { shareToGallery } from '@/services/galleryService';
import { genres } from '@/data/galleryData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Enhanced form schema with title, author, and genre fields
const shareFormSchema = z.object({
  title: z.string().min(1, "Book title is required"),
  author: z.string().min(1, "Author name is required"),
  genre: z.string().min(1, "Please select a genre"),
  agreeToTerms: z.boolean().refine(value => value === true, {
    message: "You must agree to the terms to share your cover."
  })
});

type ShareFormValues = z.infer<typeof shareFormSchema>;

interface ShareToGallerySectionProps {
  imageUrl: string;
  rating: number;
  critique: string;
  genre: string;
}

const ShareToGallerySection: React.FC<ShareToGallerySectionProps> = ({ 
  imageUrl, 
  rating, 
  critique,
  genre
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Initialize form with enhanced schema
  const form = useForm<ShareFormValues>({
    resolver: zodResolver(shareFormSchema),
    defaultValues: {
      title: "",
      author: "",
      genre: "",
      agreeToTerms: false
    }
  });

  const handleShare = async (values: ShareFormValues) => {
    setIsSubmitting(true);
    
    try {
      // First, we need to convert the base64/blob URL to a File object
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `cover-${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      // Use the galleryService to upload to Supabase with user-selected genre
      const result = await shareToGallery(
        file,
        values.title,
        values.author,
        values.genre,
        rating,
        critique
      );

      // Close dialog
      setDialogOpen(false);
      
      // Reset the form
      form.reset();
      
      // Show success toast
      toast({
        title: "Cover Shared Successfully",
        description: "Your book cover has been added to the gallery!",
        duration: 5000,
      });
      
      // Navigate to the gallery detail page for the new item
      navigate(`/gallery/${result.id}`);
    } catch (error: any) {
      console.error("Error sharing to gallery:", error);
      toast({
        title: "Sharing Failed",
        description: error.message || "There was a problem sharing your cover. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto my-8">
      <Separator className="bg-gradient-to-r from-transparent via-bookGold/50 to-transparent mb-8" />
      
      <Card className="shadow-md border-2 border-dashed border-bookNavy/20 bg-gradient-to-br from-bookCream/50 to-white">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center text-bookNavy">
            <Share2 className="mr-2 h-5 w-5 text-bookRed" />
            Share Your Cover in Our Gallery
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="w-full md:w-2/3">
              <p className="text-bookGray mb-4">
                Want to help other authors and get some free visibility? Share your results in our book design gallery! 
                We'll show your cover, our rating and a quick summary of our critique. Users can browse by rating or genre. 
                If your score is very high (or very low!) you'll appear in our leaderboard for best and worst covers. 
                This will help everyone learn how to tell a good cover from a bad one.
              </p>
              
              <Button 
                onClick={() => setDialogOpen(true)}
                className="w-full md:w-auto bg-bookNavy hover:bg-bookNavy/90 text-white"
                disabled={isSubmitting}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share to Gallery
              </Button>
            </div>
            
            <div className="w-full md:w-1/3 flex flex-col items-center">
              <div className="bg-white p-2 rounded shadow-md inline-block relative">
                <img 
                  src={imageUrl} 
                  alt="Your book cover" 
                  className="w-32 h-auto border border-gray-200"
                />
                <span className={`absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                  rating >= 7 ? 'bg-green-500' : rating <= 3 ? 'bg-red-500' : 'bg-amber-500'
                }`}>
                  {rating}
                </span>
              </div>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Share Your Cover</DialogTitle>
                <DialogDescription>
                  Fill in the details below to share your book cover to our gallery.
                </DialogDescription>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleShare)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Book Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter book title" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="author"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Author Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter author name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="genre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Genre</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Select a genre" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white z-50">
                            {genres.filter(g => g !== "All Genres").map((genreOption) => (
                              <SelectItem key={genreOption} value={genreOption}>
                                {genreOption}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="agreeToTerms"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <p className="text-sm">
                            I confirm I have rights to this cover and agree to share it publicly
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <DialogFooter className="mt-6">
                    <Button 
                      type="submit" 
                      className="bg-green-600 hover:bg-green-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sharing...
                        </>
                      ) : (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Share to Gallery
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShareToGallerySection;
